import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Star as StarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Функция для получения цвета рамки в зависимости от редкости значка
const getRarityColor = (rarity) => {
  switch (rarity) {
    case 'common':
      return '#989898';
    case 'uncommon':
      return '#58ac30';
    case 'rare':
      return '#3d85c6';
    case 'epic':
      return '#9b59b6';
    case 'legendary':
      return '#e67e22';
    default:
      return '#989898';
  }
};

/**
 * Компонент для отображения карточки серии значков
 * @param {Object} series - Объект серии значков с информацией о прогрессе
 * @param {function} onClick - Функция, которая вызывается при клике на серию
 */
const BadgeSeriesCard = ({ series, onClick }) => {
  const { t } = useTranslation(['badges', 'common']);
  
  if (!series) return null;

  return (
    <Paper
      sx={{
        p: 2,
        position: 'relative',
        backgroundColor: 'background.default',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
      }}
      elevation={0}
      onClick={onClick ? () => onClick(series) : undefined}
    >
      <Box display="flex" alignItems="center" mb={2}>
        <Avatar
          src={series.image}
          alt={series.name}
          sx={{
            width: 48,
            height: 48,
            mr: 2,
            border: series.isCompleted ? '2px solid #4caf50' : 'none',
          }}
        />
        <Box
          sx={{
            minHeight: 40,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {series.name}
          </Typography>
          {series.isCompleted ? (
            <Chip
              label={
                series.rewardClaimed
                  ? t('badges:badgeManagement.progress.rewardClaimed')
                  : t('badges:badgeManagement.progress.completed')
              }
              size="small"
              color={series.rewardClaimed ? 'success' : 'primary'}
              sx={{ mt: 0.5 }}
            />
          ) : (
            <Box sx={{ height: 24, mt: 0.5 }} />
          )}
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {series.description}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <LinearProgress
          variant="determinate"
          value={series.progress}
          sx={{
            height: 10,
            borderRadius: 6,
            mb: 1,
            bgcolor: 'background.paper',
          }}
          color={series.isCompleted ? 'success' : 'primary'}
        />
        <Typography variant="body2" color="text.secondary" align="right">
          {series.collectedBadges} / {series.totalBadges} {t('badges:badge_plural')} (
          {Math.round(series.progress)}%)
        </Typography>
      </Box>

      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
        {t('badges:badgeManagement.series.badgeCount')} ({series.badges?.length || 0}):
      </Typography>

      {/* Отображение миниатюр значков в серии */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        {series.badges?.length > 0 ? (
          series.badges.map((badge) => (
            <Tooltip
              key={badge._id}
              title={`${badge.name} ${
                badge.collected
                  ? t('badges:badgeManagement.view.yes')
                  : t('badges:badgeManagement.view.no')
              }`}
              arrow
            >
              <Avatar
                src={badge.image}
                alt={badge.name}
                sx={{
                  width: 38,
                  height: 38,
                  border: `2px solid ${getRarityColor(badge.rarity)}`,
                  opacity: badge.isSecret && !badge.collected ? 0.5 : 1,
                  filter: !badge.collected ? 'grayscale(100%)' : 'none',
                }}
              >
                {!badge.image && <StarIcon fontSize="small" />}
              </Avatar>
            </Tooltip>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('badges:badgeManagement.series.noData')}
          </Typography>
        )}
      </Box>

      {/* Бонусные награды */}
      {(series.bonusRewards?.experience > 0 ||
        series.bonusRewards?.specialBadge) && (
        <Box
          sx={{
            p: 2,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.03)'
                : '#f5f7fa',
            borderRadius: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
            {t('badges:badgeManagement.series.bonusRewards')}:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {series.bonusRewards.experience > 0 && (
              <Chip
                label={`${series.bonusRewards.experience} ${t('common:experience')}`}
                size="small"
                variant="outlined"
              />
            )}
            {series.bonusRewards.specialBadge && (
              <Chip
                avatar={
                  <Avatar
                    src={series.bonusRewards.specialBadge.image}
                    alt={series.bonusRewards.specialBadge.name}
                    sx={{ width: 22, height: 22 }}
                  />
                }
                label={t('badges:badgeManagement.series.specialBadge')}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default BadgeSeriesCard;
